import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, HomeIcon, CheckCircle2Icon, StoreIcon, PlusIcon, SearchIcon, QrCodeIcon, SendIcon } from 'lucide-react';
import { useRaastTheme, slideVariants, PAY_FROM_ACCOUNTS } from './RaastShared';
interface RaastMerchantPaymentsProps {
  onBack: () => void;
  onHome: () => void;
  onTransfer: () => void;
}
type Step = 'home' | 'list' | 'add' | 'review' | 'otp' | 'success';
export function RaastMerchantPayments({
  onBack,
  onHome,
  onTransfer
}: RaastMerchantPaymentsProps) {
  const {
    bgStyle,
    cardBg,
    inputBg,
    textPri,
    textSec,
    borderClr,
    isDark,
    isPlatinum
  } = useRaastTheme();
  const [step, setStep] = useState<Step>('home');
  const [direction, setDirection] = useState(1);
  const [payeeId, setPayeeId] = useState('');
  const [amount, setAmount] = useState('');
  const [selectedTag, setSelectedTag] = useState('Groceries');
  // OTP State
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(30);
  const tags = ['Groceries', 'Utilities', 'Dining', 'Shopping', 'Other'];
  const navigateTo = (nextStep: Step, dir: number = 1) => {
    setDirection(dir);
    setStep(nextStep);
  };
  useEffect(() => {
    if (step === 'otp') {
      const interval = setInterval(() => {
        setTimer(prev => prev > 0 ? prev - 1 : 0);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [step]);
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      const nextInput = document.getElementById(`merchant-otp-${index + 1}`);
      nextInput?.focus();
    }
    if (newOtp.every(v => v !== '')) {
      setTimeout(() => navigateTo('success'), 350);
    }
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`merchant-otp-${index - 1}`);
      prevInput?.focus();
    }
  };
  const renderHeader = (title: string, onBackClick: () => void) => <div className="px-6 pt-6 pb-4 flex items-center justify-between" data-id="element-2208">
      <button onClick={onBackClick} className={`p-2 -ml-2 rounded-full ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-2209">
        <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} strokeWidth={2} data-id="element-2210" />
      </button>
      <h1 className={`text-lg font-bold flex-1 ml-3 ${textPri}`} data-id="element-2211">{title}</h1>
      <button onClick={onHome} className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${isDark ? 'border-[#F37024] text-[#F37024] hover:bg-[#F37024]/10' : 'border-[#5A1408] text-[#5A1408] hover:bg-[#5A1408]/5'}`} data-id="element-2212">
        <HomeIcon className="w-5 h-5" strokeWidth={2} data-id="element-2213" />
      </button>
    </div>;
  const renderHome = () => <motion.div key="home" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2214">
      {renderHeader('Pay to Merchant', onBack)}
      <div className="px-6 flex-1 flex flex-col gap-4 pb-8 mt-2" data-id="element-2215">
        <button onClick={() => navigateTo('list')} className={`w-full text-left rounded-3xl p-6 relative overflow-hidden shadow-sm border ${cardBg} ${borderClr}`} data-id="element-2216">
          <div className="flex items-center justify-between" data-id="element-2217">
            <div data-id="element-2218">
              <h3 className={`font-bold text-[18px] mb-1 ${textPri}`} data-id="element-2219">
                Saved Merchants
              </h3>
              <p className={`text-[13px] ${textSec}`} data-id="element-2220">
                Pay your saved merchants
              </p>
            </div>
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isDark ? 'bg-[#3D2518]' : 'bg-[#FDF3ED]'}`} data-id="element-2221">
              <StoreIcon className="w-6 h-6 text-[#F37024]" data-id="element-2222" />
            </div>
          </div>
        </button>
        <button onClick={() => navigateTo('add')} className={`w-full text-left rounded-3xl p-6 relative overflow-hidden shadow-sm border ${cardBg} ${borderClr}`} data-id="element-2223">
          <div className="flex items-center justify-between" data-id="element-2224">
            <div data-id="element-2225">
              <h3 className={`font-bold text-[18px] mb-1 ${textPri}`} data-id="element-2226">
                Add New Payee
              </h3>
              <p className={`text-[13px] ${textSec}`} data-id="element-2227">Add a new merchant ID</p>
            </div>
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isDark ? 'bg-[#3D2518]' : 'bg-[#FDF3ED]'}`} data-id="element-2228">
              <PlusIcon className="w-6 h-6 text-[#F37024]" data-id="element-2229" />
            </div>
          </div>
        </button>
        <button onClick={onTransfer} className={`w-full text-left rounded-3xl p-6 relative overflow-hidden shadow-sm border ${cardBg} ${borderClr}`} data-id="element-2230">
          <div className="flex items-center justify-between" data-id="element-2231">
            <div data-id="element-2232">
              <h3 className={`font-bold text-[18px] mb-1 ${textPri}`} data-id="element-2233">
                RAAST Transfer
              </h3>
              <p className={`text-[13px] ${textSec}`} data-id="element-2234">Send money to a person</p>
            </div>
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isDark ? 'bg-[#3D2518]' : 'bg-[#FDF3ED]'}`} data-id="element-2235">
              <SendIcon className="w-6 h-6 text-[#F37024]" data-id="element-2236" />
            </div>
          </div>
        </button>
      </div>
    </motion.div>;
  const renderList = () => <motion.div key="list" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2237">
      {renderHeader('Saved Merchants', () => navigateTo('home', -1))}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2238">
        <div className="relative mb-6" data-id="element-2239">
          <SearchIcon className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${textSec}`} data-id="element-2240" />
          <input type="text" placeholder="Search merchants..." className={`w-full rounded-2xl py-3 pl-12 pr-4 text-[14px] outline-none border ${inputBg} ${textPri} ${borderClr}`} data-id="element-2241" />
        </div>
        <div className="space-y-4" data-id="element-2242">
          {['Al-Fatah', 'Imtiaz Super Market', 'KFC'].map((merchant, i) => <button key={i} onClick={() => {
          setPayeeId(`M-${Math.floor(Math.random() * 10000)}`);
          navigateTo('add');
        }} className={`w-full flex items-center gap-4 p-4 rounded-2xl border ${cardBg} ${borderClr}`} data-id="element-2243">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isDark ? 'bg-[#3D2518]' : 'bg-[#FDF3ED]'}`} data-id="element-2244">
                <StoreIcon className="w-6 h-6 text-[#F37024]" data-id="element-2245" />
              </div>
              <div className="text-left flex-1" data-id="element-2246">
                <p className={`text-[15px] font-bold ${textPri}`} data-id="element-2247">{merchant}</p>
                <p className={`text-[12px] ${textSec}`} data-id="element-2248">
                  Merchant ID: M-{1000 + i}
                </p>
              </div>
            </button>)}
        </div>
      </div>
    </motion.div>;
  const renderAdd = () => <motion.div key="add" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2249">
      {renderHeader('Add Payee Details', () => navigateTo('home', -1))}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2250">
        <div className="space-y-6 mb-auto" data-id="element-2251">
          <div data-id="element-2252">
            <label className={`text-[13px] font-bold mb-2 block ${textPri}`} data-id="element-2253">
              Merchant ID / Till Number
            </label>
            <input type="text" value={payeeId} onChange={e => setPayeeId(e.target.value)} placeholder="Enter ID" className={`w-full rounded-2xl py-4 px-4 text-[15px] font-medium border outline-none ${cardBg} ${textPri} ${borderClr} focus:border-[#F37024]`} data-id="element-2254" />
          </div>
          <div data-id="element-2255">
            <label className={`text-[13px] font-bold mb-2 block ${textPri}`} data-id="element-2256">
              Amount (PKR)
            </label>
            <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" className={`w-full rounded-2xl py-4 px-4 text-[15px] font-medium border outline-none ${cardBg} ${textPri} ${borderClr} focus:border-[#F37024]`} data-id="element-2257" />
          </div>
          <div data-id="element-2258">
            <label className={`text-[13px] font-bold mb-2 block ${textPri}`} data-id="element-2259">
              Tag
            </label>
            <div className="flex flex-wrap gap-2" data-id="element-2260">
              {tags.map(tag => <button key={tag} onClick={() => setSelectedTag(tag)} className={`px-4 py-2 rounded-full text-[13px] font-bold transition-colors border ${selectedTag === tag ? 'bg-[#F37024] text-white border-[#F37024]' : `${cardBg} ${textSec} ${borderClr}`}`} data-id="element-2261">
                  {tag}
                </button>)}
            </div>
          </div>
        </div>
        <button onClick={() => navigateTo('review')} disabled={!payeeId || !amount} className="w-full py-4 rounded-2xl font-bold text-[16px] bg-[#F37024] text-white shadow-md disabled:opacity-50 mt-6" data-id="element-2262">
          Next
        </button>
      </div>
    </motion.div>;
  const renderReview = () => <motion.div key="review" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2263">
      {renderHeader('Review Payment', () => navigateTo('add', -1))}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2264">
        <div className={`rounded-[24px] p-6 shadow-sm mb-auto border ${cardBg} ${borderClr}`} data-id="element-2265">
          <div className="flex flex-col items-center mb-8 text-center" data-id="element-2266">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-3 ${isDark ? 'bg-[#3D2518]' : 'bg-[#FDF3ED]'}`} data-id="element-2267">
              <StoreIcon className="w-8 h-8 text-[#F37024]" data-id="element-2268" />
            </div>
            <p className={`text-[28px] font-bold ${textPri}`} data-id="element-2269">
              Rs. {parseInt(amount || '0').toLocaleString()}
            </p>
            <p className={`text-[14px] mt-1 ${textSec}`} data-id="element-2270">
              To Merchant ID: {payeeId}
            </p>
          </div>
          <div className="space-y-4" data-id="element-2271">
            <div className="flex justify-between" data-id="element-2272">
              <span className={textSec} data-id="element-2273">Tag</span>
              <span className={`font-bold ${textPri}`} data-id="element-2274">{selectedTag}</span>
            </div>
            <div className="flex justify-between" data-id="element-2275">
              <span className={textSec} data-id="element-2276">Fee</span>
              <span className={`font-bold ${textPri}`} data-id="element-2277">Rs. 0</span>
            </div>
            <div className={`h-px w-full ${borderClr} my-2`} data-id="element-2278" />
            <div className="flex justify-between" data-id="element-2279">
              <span className={`font-bold ${textPri}`} data-id="element-2280">Total Amount</span>
              <span className={`font-bold text-[#F37024]`} data-id="element-2281">
                Rs. {parseInt(amount || '0').toLocaleString()}
              </span>
            </div>
          </div>
        </div>
        <button onClick={() => navigateTo('otp')} className="w-full py-4 rounded-2xl font-bold text-[16px] bg-[#F37024] text-white shadow-md mt-6" data-id="element-2282">
          Confirm Payment
        </button>
      </div>
    </motion.div>;
  const renderOtp = () => <motion.div key="otp" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2283">
      {renderHeader('Enter OTP', () => navigateTo('review', -1))}
      <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-2284">
        <div className="mb-8 text-center" data-id="element-2285">
          <p className={`text-[14px] ${textSec}`} data-id="element-2286">
            We've sent a 6-digit code to your registered mobile number
          </p>
        </div>
        <div className="flex justify-between gap-2 mb-8" data-id="element-2287">
          {otp.map((digit, i) => <input key={i} id={`merchant-otp-${i}`} type="password" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpChange(i, e.target.value)} onKeyDown={e => handleOtpKeyDown(i, e)} className={`w-12 h-14 rounded-xl text-center text-[24px] font-bold border-2 outline-none transition-colors ${digit ? 'border-[#F37024] bg-[#F37024]/10 text-[#F37024]' : `${borderClr} ${cardBg} ${textPri} focus:border-[#F37024]/50`}`} data-id="element-2288" />)}
        </div>
        <div className="text-center mb-auto" data-id="element-2289">
          {timer > 0 ? <p className={`text-[14px] font-medium ${textSec}`} data-id="element-2290">
              Resend OTP in 0:{timer.toString().padStart(2, '0')}
            </p> : <button onClick={() => setTimer(30)} className="text-[14px] font-bold text-[#F37024]" data-id="element-2291">
              Resend OTP
            </button>}
        </div>
      </div>
    </motion.div>;
  const renderSuccess = () => <motion.div key="success" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className={`w-full min-h-screen flex flex-col ${bgStyle}`} data-id="element-2292">
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12" data-id="element-2293">
        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        damping: 15
      }} className="w-24 h-24 bg-[#22C55E] rounded-full flex items-center justify-center mb-6 shadow-lg shadow-[#22C55E]/30" data-id="element-2294">
          <CheckCircle2Icon className="w-12 h-12 text-white" strokeWidth={2.5} data-id="element-2295" />
        </motion.div>
        <motion.h2 initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className={`text-2xl font-bold mb-2 text-center ${textPri}`} data-id="element-2296">
          Payment Successful!
        </motion.h2>
        <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className={`text-[15px] text-center mb-8 ${textSec}`} data-id="element-2297">
          Your payment of{' '}
          <span className="font-bold text-[#F37024]" data-id="element-2298">
            Rs. {parseInt(amount || '0').toLocaleString()}
          </span>{' '}
          to Merchant {payeeId} has been completed.
        </motion.p>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className={`w-full rounded-[24px] p-6 shadow-sm border ${cardBg} ${borderClr}`} data-id="element-2299">
          <div className="space-y-4" data-id="element-2300">
            <div className="flex justify-between" data-id="element-2301">
              <span className={textSec} data-id="element-2302">Reference ID</span>
              <span className={`font-bold ${textPri}`} data-id="element-2303">
                RST-{Math.floor(Math.random() * 1000000000)}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-2304">
              <span className={textSec} data-id="element-2305">Date & Time</span>
              <span className={`font-bold ${textPri}`} data-id="element-2306">
                {new Date().toLocaleString()}
              </span>
            </div>
          </div>
        </motion.div>
      </div>
      <div className="px-6 pb-8" data-id="element-2307">
        <button onClick={onTransfer} className="w-full py-4 rounded-2xl font-bold text-[16px] bg-[#F37024] text-white shadow-md mb-4" data-id="element-2308">
          TRANSFER NOW
        </button>
        <button onClick={onHome} className={`w-full py-4 rounded-2xl font-bold text-[16px] border-2 ${isDark ? 'border-[rgba(243,112,36,0.3)] text-[#F37024]' : 'border-[#F0E8E3] text-[#303030]'}`} data-id="element-2309">
          Done
        </button>
      </div>
    </motion.div>;
  return <AnimatePresence mode="wait" custom={direction} data-id="element-2310">
      {step === 'home' && renderHome()}
      {step === 'list' && renderList()}
      {step === 'add' && renderAdd()}
      {step === 'review' && renderReview()}
      {step === 'otp' && renderOtp()}
      {step === 'success' && renderSuccess()}
    </AnimatePresence>;
}